import { FC, useMemo } from 'react';
import { sanitizeHtml } from '@new/common/lib/sanitize';
import { toast } from 'react-toastify';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Can, useAbility } from '@new/casl';
import { useUploadFileMutation, useCreateUserProjectMutation } from '@teachur/http-client';
import { lessonsApi, useGetUserProjectsQuery } from '@teachur/http-client';
import { createFileData, mapFileToItem } from '@new/features/lessons/lib/fileDataHelper';
import { getErrorMessage } from '@new/lib';

import { FileSection } from '@new/features/lessons/components/FileSection/FileSection';
import { FileItem } from '@new/features/lessons/components/FileCard/FileCard';
import { Upload } from '@new/common/ui/Upload/Upload';
import { ExampleFileLink } from '@new/common/ui/ExampleFileLink/ExampleFileLink';
import { StepMessage } from '@new/features/courses/components/CourseCompletionSteps/StepContent/StepMessage';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';

interface LessonExerciseTabProps {
  lesson: Lesson;
}

export const LessonExerciseTab: FC<LessonExerciseTabProps> = ({ lesson }) => {
  const projects = lesson?.projects ?? [];

  return (
    <div className="flex flex-col gap-10">
      <div className="bg-(--neutral-black-NB50) p-4 rounded-lg">
        <StepMessage
          variant="warning"
          message="Exercises are optional and not reviewed; they are designed to strengthen your mastery of lesson objectives."
        />
      </div>

      {projects.map((project, index) => (
        <div key={project.id} className="flex flex-col gap-6">
          {index > 0 && <div className="h-[1.5px] bg-(--neutral-black-NB200)" />}
          <LessonExerciseItem lesson={lesson} project={project} />
        </div>
      ))}
    </div>
  );
};

interface LessonExerciseItemProps {
  lesson: Lesson;
  project: Project;
}

const LessonExerciseItem: FC<LessonExerciseItemProps> = ({ lesson, project }) => {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const uploadFileMutation = useUploadFileMutation();
  const requireSubscribed = useRequireSubscribed();

  const canUpdateLesson = ability.can('update', 'Lesson');

  const { data: userProjectsResponse } = useGetUserProjectsQuery({
    projectId: project.id,
  });

  const userProjects = userProjectsResponse?.data || [];

  const updateLessonMutation = useMutation({
    mutationFn: (updatedLesson: any) => lessonsApi.update(lesson.id, updatedLesson),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lesson', lesson.id] });
    },
  });

  const createUserProjectMutation = useCreateUserProjectMutation();

  const lessonFiles = useMemo(
    () =>
      project?.files
        ?.map((file: any) => mapFileToItem(file))
        .filter((f): f is FileItem => f !== null) || [],
    [project]
  );

  const userExerciseFiles = useMemo(
    () =>
      userProjects
        .flatMap((up) =>
          (up.submissions ?? []).map((submission) => ({
            ...mapFileToItem(submission.file),
            userProjectId: up.id,
          }))
        )
        .filter((f) => f.url),
    [userProjects]
  );

  const handleFileUpload = requireSubscribed(async (file: File) => {
    try {
      const uploadedFileData = await uploadFileMutation.mutateAsync(file);

      if (canUpdateLesson) {
        const updatedProjects = (lesson.projects || []).map((p) =>
          p.id === project.id
            ? { ...p, files: [...(p.files || []), createFileData(uploadedFileData, file.name)] }
            : p
        );
        const updatedLesson = {
          ...lesson,
          projects: updatedProjects,
        };
        await updateLessonMutation.mutateAsync(updatedLesson);
        queryClient.invalidateQueries({ queryKey: ['lesson', lesson.id] });
      } else {
        await createUserProjectMutation.mutateAsync({
          projectId: project.id,
          file: uploadedFileData,
        });
      }

      toast.success('File uploaded successfully!');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to upload file'));
    }
  });

  const handleDownloadFile = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col gap-6">
      <h3 className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB1000)! font-bold m-0!">
        {project?.name}
      </h3>

      {project?.description && (
        <div className="flex flex-col gap-3">
          <h4 className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
            Exercise description
          </h4>
          <div
            className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB900)! [line-height:150%]"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(project.description) }}
          />
        </div>
      )}

      <FileSection title="Exercise files" files={lessonFiles} onDownload={handleDownloadFile} />

      {project?.exampleFileUrl && <ExampleFileLink url={project.exampleFileUrl} />}

      {!canUpdateLesson && userExerciseFiles.length > 0 && (
        <div className="flex flex-col gap-3">
          <h4 className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
            Your exercise
          </h4>
          <FileSection
            title=""
            files={userExerciseFiles}
            onDownload={handleDownloadFile}
            clickable
          />
        </div>
      )}

      <Can I="enroll" a="Degree">
        <div className="flex flex-col gap-3">
          <Upload
            onUpload={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip"
            disabled={uploadFileMutation.isPending}
            isUploading={uploadFileMutation.isPending}
            uploadText="Drag & Drop your exercise files here or choose files"
            supportedFormatsText="(PDF, JPEG, JPG, DOC, DOCX, HTML)"
            maxSizeMB={10}
            showFilePreview={false}
          />
        </div>
      </Can>
    </div>
  );
};
